import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  IconButton,
  LinearProgress,
  Snackbar,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RedoIcon from '@mui/icons-material/Redo';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import UndoIcon from '@mui/icons-material/Undo';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import MainLayout from '../../../layout/MainLayout';

import ReactFlow, {
  Background,
  Controls,
  MarkerType,
  MiniMap,
} from 'reactflow';
import 'reactflow/dist/style.css';

import CohortNode from './graph/CohortNode';
import CriteriaParameterAutocomplete from './graph/CriteriaParameterAutocomplete';
import { layoutGraph } from './graph/layout';
import { demoEdges, demoNodes, generateDemoTree } from './graph/mockGraph';
import { GraphStateProvider, useGraphState } from './graph/state/GraphStateContext';
import {
  buildGraphStateFromReactFlow,
  computeGraphStatistics,
  convertGraphStateToReactFlow,
  createEmptyGraphState,
  deserializeImportedGraph,
  getDescendantIds,
  GRAPH_ACTIONS,
  reduceGraphAction,
  serializeGraphForExport,
} from './graph/state/graphModel';

const CRITERIA_OPERATORS = [
  { value: '=', label: '=' },
  { value: '!=', label: '!=' },
  { value: '<', label: '<' },
  { value: '>', label: '>' },
  { value: '<=', label: '<=' },
  { value: '>=', label: '>=' },
  { value: 'CONTAINS', label: 'CONTAINS' },
  { value: 'IN', label: 'IN' },
];

const HISTORY_LIMIT = 50;
const DRAFT_STORAGE_KEY = 'olymp-cohort-graph-draft-v1';
const TOUR_STORAGE_KEY = 'olymp-cohort-graph-tour-seen-v1';
const SNAPSHOT_NODE_WIDTH = 260;
const SNAPSHOT_NODE_HEIGHT = 110;

const cloneGraphSnapshot = (graphState) => JSON.parse(JSON.stringify(graphState));

const readDraftGraphState = () => {
  if (typeof window === 'undefined') {
    return createEmptyGraphState('Cohort Graph Draft');
  }

  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return createEmptyGraphState('Cohort Graph Draft');

    return deserializeImportedGraph(JSON.parse(raw));
  } catch {
    return createEmptyGraphState('Cohort Graph Draft');
  }
};

const downloadGraphJson = (payload) => {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `cohort-graph-${stamp}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const buildGraphSnapshotSvg = (flowNodes, flowEdges) => {
  const nodes = Array.isArray(flowNodes) ? flowNodes : [];
  const edges = Array.isArray(flowEdges) ? flowEdges : [];
  if (nodes.length === 0) {
    throw new Error('No nodes available for export.');
  }

  const byId = {};
  nodes.forEach((node) => {
    byId[node.id] = node;
  });

  const xValues = nodes.map((node) => Number(node?.position?.x) || 0);
  const yValues = nodes.map((node) => Number(node?.position?.y) || 0);
  const minX = Math.min(...xValues);
  const minY = Math.min(...yValues);
  const maxX = Math.max(...xValues);
  const maxY = Math.max(...yValues);
  const margin = 48;

  const width = Math.max(720, Math.round(maxX - minX + SNAPSHOT_NODE_WIDTH + margin * 2));
  const height = Math.max(420, Math.round(maxY - minY + SNAPSHOT_NODE_HEIGHT + margin * 2));

  const mapX = (x) => Math.round((Number(x) || 0) - minX + margin);
  const mapY = (y) => Math.round((Number(y) || 0) - minY + margin);

  const statusColor = (status) => {
    if (status === 'executed' || status === 'completed') return '#48BB78';
    if (status === 'executing') return '#F6AD55';
    if (status === 'edited') return '#4299E1';
    if (status === 'error') return '#F56565';
    return '#CBD5E0';
  };

  const edgeSvg = edges
    .map((edge) => {
      const source = byId[edge.source];
      const target = byId[edge.target];
      if (!source || !target) return '';

      const sourceX = mapX(source.position?.x) + SNAPSHOT_NODE_WIDTH / 2;
      const sourceY = mapY(source.position?.y) + SNAPSHOT_NODE_HEIGHT / 2;
      const targetX = mapX(target.position?.x) + SNAPSHOT_NODE_WIDTH / 2;
      const targetY = mapY(target.position?.y) + SNAPSHOT_NODE_HEIGHT / 2;

      return `<line x1="${sourceX}" y1="${sourceY}" x2="${targetX}" y2="${targetY}" stroke="#A0AEC0" stroke-width="2" marker-end="url(#arrow)" />`;
    })
    .join('\n');

  const nodeSvg = nodes
    .map((node) => {
      const x = mapX(node.position?.x);
      const y = mapY(node.position?.y);
      const label = escapeXml(node.data?.label || `Node ${node.id}`);
      const status = escapeXml(node.data?.status || 'unedited');
      const statusText = status.charAt(0).toUpperCase() + status.slice(1);
      const include = Number(node.data?.criteriaCount?.include) || 0;
      const exclude = Number(node.data?.criteriaCount?.exclude) || 0;
      const patients =
        Number.isFinite(node.data?.patientCount) &&
        (node.data?.status === 'executed' ||
          node.data?.status === 'completed' ||
          node.data?.isExecuted === true)
          ? Number(node.data.patientCount).toLocaleString()
          : null;
      const border = statusColor(node.data?.status);

      return `
        <g transform="translate(${x}, ${y})">
          <rect x="0" y="0" rx="12" ry="12" width="${SNAPSHOT_NODE_WIDTH}" height="${SNAPSHOT_NODE_HEIGHT}" fill="#FFFFFF" stroke="${border}" stroke-width="2" />
          <text x="14" y="26" font-size="16" font-family="Inter, Arial, sans-serif" font-weight="700" fill="#2D3748">${label}</text>
          <text x="14" y="48" font-size="12" font-family="Inter, Arial, sans-serif" fill="#718096">${include} Include, ${exclude} Exclude</text>
          <text x="14" y="68" font-size="12" font-family="Inter, Arial, sans-serif" fill="${border}" font-weight="700">${escapeXml(statusText)}</text>
          ${
            patients
              ? `<text x="${SNAPSHOT_NODE_WIDTH - 14}" y="68" text-anchor="end" font-size="12" font-family="Inter, Arial, sans-serif" fill="#2D3748" font-weight="700">${escapeXml(patients)} patients</text>`
              : ''
          }
        </g>
      `;
    })
    .join('\n');

  return {
    width,
    height,
    svg: `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <defs>
    <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
      <path d="M0,0 L0,6 L9,3 z" fill="#A0AEC0" />
    </marker>
  </defs>
  <rect x="0" y="0" width="${width}" height="${height}" fill="#F8F9FA" />
  ${edgeSvg}
  ${nodeSvg}
</svg>`,
  };
};

const downloadSnapshotSvg = (flowNodes, flowEdges) => {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const { svg } = buildGraphSnapshotSvg(flowNodes, flowEdges);
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `cohort-graph-${stamp}.svg`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const downloadSnapshotPng = async (flowNodes, flowEdges) => {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const { svg, width, height } = buildGraphSnapshotSvg(flowNodes, flowEdges);
  const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Could not render graph snapshot image.'));
      image.src = svgUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas context is not available.');
    }
    context.drawImage(img, 0, 0);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((nextBlob) => resolve(nextBlob), 'image/png');
    });
    if (!blob) {
      throw new Error('Could not export PNG.');
    }

    const pngUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = pngUrl;
    anchor.download = `cohort-graph-${stamp}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(pngUrl);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
};

const sleep = (ms) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const getPathFromRootToNode = (graphState, nodeId) => {
  const nodesById = graphState?.nodesById || {};
  const rootId = graphState?.rootNodeId || null;
  if (!nodeId || !rootId || !nodesById[nodeId]) return null;

  const visited = new Set();
  const path = [];
  let currentId = nodeId;

  while (currentId) {
    if (visited.has(currentId)) return null;
    visited.add(currentId);
    path.push(currentId);
    if (currentId === rootId) break;
    currentId = nodesById[currentId]?.parentId || null;
  }

  if (path[path.length - 1] !== rootId) return null;
  return path.reverse();
};

const buildExecutionPlan = (graphState, targetNodeId = null) => {
  const nodesById = graphState?.nodesById || {};
  const rootId = graphState?.rootNodeId || null;
  if (!rootId || !nodesById[rootId]) return [];

  let allowedIds = null;
  if (targetNodeId) {
    const path = getPathFromRootToNode(graphState, targetNodeId);
    if (!path) return [];
    allowedIds = new Set(path);
  }

  const queue = [rootId];
  const visited = new Set();
  const executionOrder = [];

  while (queue.length > 0) {
    const currentId = queue.shift();
    if (!currentId || visited.has(currentId)) continue;
    if (allowedIds && !allowedIds.has(currentId)) continue;
    visited.add(currentId);
    executionOrder.push(currentId);

    if (targetNodeId && currentId === targetNodeId) continue;

    const children = nodesById[currentId]?.childIds || [];
    children.forEach((childId) => {
      if (!visited.has(childId)) queue.push(childId);
    });
  }

  return executionOrder;
};

const CohortGraphBuilderContent = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { graphState, dispatchGraph } = useGraphState();

  const graphStateRef = useRef(graphState);
  const importFileInputRef = useRef(null);
  const criterionIdRef = useRef(0);

  const [layoutDirection, setLayoutDirection] = useState('LR');
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [historyPast, setHistoryPast] = useState([]);
  const [historyFuture, setHistoryFuture] = useState([]);

  const [editNodeId, setEditNodeId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editStatus, setEditStatus] = useState('unedited');
  const [editIncludeCriteria, setEditIncludeCriteria] = useState([]);
  const [editExcludeCriteria, setEditExcludeCriteria] = useState([]);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    nodeId: null,
    nodeLabel: '',
    descendantIds: [],
  });
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [executionTargetNodeId, setExecutionTargetNodeId] = useState(null);
  const [executionMode, setExecutionMode] = useState('full');
  const [executionLogs, setExecutionLogs] = useState([]);
  const [lastExecutionAt, setLastExecutionAt] = useState(graphState.metadata?.lastExecutedAt || null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    severity: 'info',
    message: '',
  });
  const executionRunIdRef = useRef(0);

  useEffect(() => {
    graphStateRef.current = graphState;
  }, [graphState]);

  useEffect(() => {
    setLastExecutionAt(graphState.metadata?.lastExecutedAt || null);
  }, [graphState.metadata]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const payload = serializeGraphForExport(graphState);
      window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Ignore draft save errors in local dev.
    }
  }, [graphState]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const alreadySeen = window.localStorage.getItem(TOUR_STORAGE_KEY);
    setShowWelcomeTour(!alreadySeen);
  }, []);

  const showFeedback = useCallback((message, severity = 'success') => {
    setFeedback({
      open: true,
      severity,
      message,
    });
  }, []);

  const handleCloseFeedback = useCallback((_, reason) => {
    if (reason === 'clickaway') return;
    setFeedback((current) => ({ ...current, open: false }));
  }, []);

  const handleDismissWelcomeTour = useCallback(() => {
    setShowWelcomeTour(false);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(TOUR_STORAGE_KEY, '1');
    }
  }, []);

  const persistDraftNow = useCallback(() => {
    if (typeof window === 'undefined') return;

    try {
      const payload = serializeGraphForExport(graphStateRef.current);
      window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
      showFeedback('Draft saved to local storage.', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save draft.';
      showFeedback(`Could not save draft: ${message}`, 'error');
    }
  }, [showFeedback]);

  const getNextCriterionId = useCallback(() => {
    criterionIdRef.current += 1;
    return `criterion-${criterionIdRef.current}`;
  }, []);

  const getNextNodeId = useCallback(() => {
    const nodeIds = Object.keys(graphStateRef.current.nodesById || {});
    const numericIds = nodeIds
      .map((nodeId) => Number.parseInt(nodeId, 10))
      .filter((value) => Number.isFinite(value));

    const nextNumericId = numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1;
    return String(nextNumericId);
  }, []);

  const applyGraphAction = useCallback(
    (action, options = {}) => {
      const { recordHistory = true } = options;
      const previousState = graphStateRef.current;
      const preview = reduceGraphAction(previousState, action);

      if (!preview.valid) {
        dispatchGraph(action);
        if (preview.nextState?.lastError) {
          showFeedback(preview.nextState.lastError, 'error');
        }
        return false;
      }

      if (recordHistory) {
        const snapshot = cloneGraphSnapshot(previousState);
        setHistoryPast((prev) => [...prev.slice(-(HISTORY_LIMIT - 1)), snapshot]);
        setHistoryFuture([]);
      }

      dispatchGraph(action);
      return true;
    },
    [dispatchGraph, showFeedback],
  );

  const normalizeCriteriaForEdit = useCallback(
    (criteriaArray, fallbackCount = 0, group = 'include') => {
      if (Array.isArray(criteriaArray) && criteriaArray.length > 0) {
        return criteriaArray.map((criterion, index) => {
          if (typeof criterion === 'string') {
            return {
              id: getNextCriterionId(),
              field: criterion,
              operator: '=',
              value: '',
            };
          }

          if (criterion && typeof criterion === 'object') {
            const field = String(
              criterion.parameterName ||
                criterion.field ||
                criterion.label ||
                criterion.parameter ||
                criterion.name ||
                `Criterion ${index + 1}`,
            );
            const value = String(criterion.value || criterion.term || criterion.targetValue || '');
            const rawOperator = String(criterion.operator || '=').trim();
            const normalizedLookup = rawOperator.toLowerCase();
            const operator =
              normalizedLookup === 'equals'
                ? '='
                : normalizedLookup === 'not_equals'
                  ? '!='
                  : normalizedLookup === 'gte'
                    ? '>='
                    : normalizedLookup === 'lte'
                      ? '<='
                      : normalizedLookup === 'contains'
                        ? 'CONTAINS'
                        : normalizedLookup === 'in'
                          ? 'IN'
                          : rawOperator.toUpperCase();

            return {
              id: getNextCriterionId(),
              field,
              operator: CRITERIA_OPERATORS.some((entry) => entry.value === operator) ? operator : '=',
              value,
            };
          }

          return {
            id: getNextCriterionId(),
            field: `Criterion ${index + 1}`,
            operator: '=',
            value: '',
          };
        });
      }

      const safeCount = Number.isFinite(fallbackCount) ? fallbackCount : 0;
      return Array.from({ length: Math.max(0, safeCount) }, (_, index) => ({
        id: getNextCriterionId(),
        field: `${group === 'include' ? 'Include' : 'Exclude'} criterion ${index + 1}`,
        operator: '=',
        value: '',
      }));
    },
    [getNextCriterionId],
  );

  const editingNode = useMemo(
    () => (editNodeId ? graphState.nodesById?.[editNodeId] || null : null),
    [editNodeId, graphState.nodesById],
  );

  const renderGraph = useMemo(() => {
    const { nodes: flowNodes, edges: flowEdges } = convertGraphStateToReactFlow(graphState);
    return layoutGraph(flowNodes, flowEdges, { direction: layoutDirection });
  }, [graphState, layoutDirection]);

  const nodes = renderGraph.nodes;
  const edges = renderGraph.edges;

  const graphStatistics = useMemo(() => computeGraphStatistics(graphState), [graphState]);
  const breakpointNodeId = graphState.metadata?.breakpointNodeId || null;
  const breakpointNodeLabel = breakpointNodeId
    ? graphState.nodesById?.[breakpointNodeId]?.name || `Node ${breakpointNodeId}`
    : null;

  const handleCloseEdit = useCallback(() => {
    setEditNodeId(null);
    setEditLabel('');
    setEditStatus('unedited');
    setEditIncludeCriteria([]);
    setEditExcludeCriteria([]);
  }, []);

  const handleEditNode = useCallback(
    (nodeId) => {
      const node = graphStateRef.current.nodesById?.[nodeId];
      if (!node) return;

      setEditNodeId(nodeId);
      setEditLabel(node.name || '');
      setEditStatus(node.status || 'unedited');
      setEditIncludeCriteria(normalizeCriteriaForEdit(node.includeConditions, undefined, 'include'));
      setEditExcludeCriteria(normalizeCriteriaForEdit(node.excludeConditions, undefined, 'exclude'));
    },
    [normalizeCriteriaForEdit],
  );

  const handleRenameNode = useCallback(
    (nodeId, nextLabel) => {
      const trimmed = String(nextLabel || '').trim();
      if (!trimmed) return;

      applyGraphAction(
        {
          type: GRAPH_ACTIONS.UPDATE_NODE,
          payload: {
            nodeId,
            patch: {
              name: trimmed,
            },
          },
        },
        { recordHistory: true },
      );
    },
    [applyGraphAction],
  );

  const handleRequestDeleteNode = useCallback((nodeId) => {
    const current = graphStateRef.current;
    const node = current.nodesById?.[nodeId];
    if (!node) return;

    const descendants = getDescendantIds(current, nodeId);
    setDeleteDialog({
      open: true,
      nodeId,
      nodeLabel: node.name || 'this node',
      descendantIds: descendants,
    });
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialog({ open: false, nodeId: null, nodeLabel: '', descendantIds: [] });
  }, []);

  const handleConfirmDeleteNode = useCallback(
    (cascadeDelete) => {
      const { nodeId, descendantIds } = deleteDialog;
      if (!nodeId) return;

      if (!cascadeDelete && descendantIds.length > 0) {
        showFeedback(
          'Delete only this node is not allowed because it would orphan child nodes. Use "Delete Node + Descendants".',
          'warning',
        );
        return;
      }

      const changed = applyGraphAction(
        {
          type: GRAPH_ACTIONS.DELETE_NODE,
          payload: {
            nodeId,
            cascade: cascadeDelete,
          },
        },
        { recordHistory: true },
      );

      if (!changed) return;

      if (editNodeId && (nodeId === editNodeId || descendantIds.includes(editNodeId))) {
        handleCloseEdit();
      }
      handleCloseDeleteDialog();
      showFeedback(
        cascadeDelete
          ? `Deleted node and ${descendantIds.length} descendant node${descendantIds.length === 1 ? '' : 's'}.`
          : 'Node deleted successfully.',
        'success',
      );
    },
    [applyGraphAction, deleteDialog, editNodeId, handleCloseDeleteDialog, handleCloseEdit, showFeedback],
  );

  const handleCreateRootNode = useCallback(() => {
    const currentNodeCount = Object.keys(graphStateRef.current.nodesById || {}).length;
    if (currentNodeCount > 0) {
      showFeedback('Graph already contains nodes. Clear the graph before creating a new root.', 'warning');
      return;
    }

    const rootId = getNextNodeId();
    const changed = applyGraphAction(
      {
        type: GRAPH_ACTIONS.ADD_NODE,
        payload: {
          id: rootId,
          parentId: null,
          node: {
            id: rootId,
            name: 'Root Cohort',
            status: 'unedited',
          },
        },
      },
      { recordHistory: true },
    );
    if (changed) {
      showFeedback('Root node created.', 'success');
    }
  }, [applyGraphAction, getNextNodeId, showFeedback]);

  const handleAddChild = useCallback(
    (parentId) => {
      const childId = getNextNodeId();
      const changed = applyGraphAction(
        {
          type: GRAPH_ACTIONS.ADD_NODE,
          payload: {
            id: childId,
            parentId,
            node: {
              id: childId,
              name: 'New Cohort',
              status: 'unedited',
            },
          },
        },
        { recordHistory: true },
      );
      if (changed) {
        showFeedback('Child node added.', 'success');
      }
    },
    [applyGraphAction, getNextNodeId, showFeedback],
  );

  const handleInsertBetween = useCallback(
    (childId) => {
      const current = graphStateRef.current;
      const child = current.nodesById?.[childId];
      if (!child || !child.parentId) {
        showFeedback(
          'Insert is only available for nodes that already have a parent (non-root nodes).',
          'warning',
        );
        return;
      }

      const insertedId = getNextNodeId();
      const changed = applyGraphAction(
        {
          type: GRAPH_ACTIONS.ADD_NODE,
          payload: {
            id: insertedId,
            parentId: child.parentId,
            insertBeforeChildId: childId,
            node: {
              id: insertedId,
              name: 'Inserted Cohort',
              status: 'unedited',
            },
          },
        },
        { recordHistory: true },
      );
      if (changed) {
        showFeedback('Node inserted between parent and child.', 'success');
      }
    },
    [applyGraphAction, getNextNodeId, showFeedback],
  );

  const handleToggleBreakpoint = useCallback(
    (nodeId) => {
      const current = graphStateRef.current;
      if (!current.nodesById?.[nodeId]) return;

      const nextBreakpointNodeId = current.metadata?.breakpointNodeId === nodeId ? null : nodeId;
      const changed = applyGraphAction(
        {
          type: GRAPH_ACTIONS.SET_GRAPH,
          payload: {
            ...current,
            metadata: {
              ...current.metadata,
              breakpointNodeId: nextBreakpointNodeId,
            },
          },
        },
        { recordHistory: true },
      );
      if (changed) {
        showFeedback(
          nextBreakpointNodeId ? `Breakpoint set on node ${nodeId}.` : 'Breakpoint removed.',
          'info',
        );
      }
    },
    [applyGraphAction, showFeedback],
  );

  const handleUndo = useCallback(() => {
    setHistoryPast((past) => {
      if (past.length === 0) return past;
      const previous = past[past.length - 1];
      const currentSnapshot = cloneGraphSnapshot(graphStateRef.current);

      setHistoryFuture((future) => [...future, currentSnapshot]);
      dispatchGraph({
        type: GRAPH_ACTIONS.SET_GRAPH,
        payload: previous,
      });

      return past.slice(0, -1);
    });
  }, [dispatchGraph]);

  const handleRedo = useCallback(() => {
    setHistoryFuture((future) => {
      if (future.length === 0) return future;
      const next = future[future.length - 1];
      const currentSnapshot = cloneGraphSnapshot(graphStateRef.current);

      setHistoryPast((past) => [...past.slice(-(HISTORY_LIMIT - 1)), currentSnapshot]);
      dispatchGraph({
        type: GRAPH_ACTIONS.SET_GRAPH,
        payload: next,
      });

      return future.slice(0, -1);
    });
  }, [dispatchGraph]);

  const handleAddCriterion = useCallback(
    (group) => {
      const nextCriterion = {
        id: getNextCriterionId(),
        field: '',
        operator: '=',
        value: '',
      };

      if (group === 'include') {
        setEditIncludeCriteria((items) => [...items, nextCriterion]);
      } else {
        setEditExcludeCriteria((items) => [...items, nextCriterion]);
      }
    },
    [getNextCriterionId],
  );

  const handleUpdateCriterion = useCallback((group, criterionId, key, nextValue) => {
    const updater = (items) =>
      items.map((item) =>
        item.id === criterionId
          ? {
              ...item,
              [key]: nextValue,
            }
          : item,
      );

    if (group === 'include') {
      setEditIncludeCriteria(updater);
    } else {
      setEditExcludeCriteria(updater);
    }
  }, []);

  const handleDeleteCriterion = useCallback((group, criterionId) => {
    const updater = (items) => items.filter((item) => item.id !== criterionId);

    if (group === 'include') {
      setEditIncludeCriteria(updater);
    } else {
      setEditExcludeCriteria(updater);
    }
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editNodeId) return;
    const nextName = String(editLabel || '').trim();
    if (!nextName) return;

    const normalizeForSave = (items) =>
      items
        .map((item) => ({
          parameterName: String(item.field || '').trim(),
          operator: String(item.operator || '='),
          value: String(item.value || '').trim(),
          logicalOperator: 'AND',
        }))
        .filter((item) => item.parameterName.length > 0 || item.value.length > 0);

    const includeConditions = normalizeForSave(editIncludeCriteria);
    const excludeConditions = normalizeForSave(editExcludeCriteria);

    const hasCriteria = includeConditions.length > 0 || excludeConditions.length > 0;
    const resolvedStatus = editStatus === 'unedited' && hasCriteria ? 'edited' : editStatus;
    const executed = resolvedStatus === 'executed' || resolvedStatus === 'completed';
    const currentPatientCount = graphStateRef.current.nodesById?.[editNodeId]?.patientCount;

    const changed = applyGraphAction(
      {
        type: GRAPH_ACTIONS.UPDATE_NODE,
        payload: {
          nodeId: editNodeId,
          patch: {
            name: nextName,
            status: resolvedStatus,
            includeConditions,
            excludeConditions,
            isExecuted: executed,
            patientCount: executed
              ? Number.isFinite(currentPatientCount)
                ? Number(currentPatientCount)
                : 123
              : null,
          },
        },
      },
      { recordHistory: true },
    );

    if (changed) {
      handleCloseEdit();
      showFeedback('Node updated.', 'success');
    }
  }, [
    applyGraphAction,
    editExcludeCriteria,
    editIncludeCriteria,
    editLabel,
    editNodeId,
    editStatus,
    handleCloseEdit,
    showFeedback,
  ]);

  const appendExecutionLog = useCallback((message) => {
    const timestamp = new Date().toLocaleTimeString();
    setExecutionLogs((previous) => [`[${timestamp}] ${message}`, ...previous].slice(0, 24));
  }, []);

  const runExecutionSimulation = useCallback(
    async (targetNodeId = null, mode = 'full') => {
      if (isExecuting) return;

      const currentGraph = graphStateRef.current;
      const nodeIds = Object.keys(currentGraph.nodesById || {});
      if (nodeIds.length === 0) {
        showFeedback('Please create or load a graph before executing.', 'warning');
        return;
      }

      const executionPlan = buildExecutionPlan(currentGraph, targetNodeId);
      if (executionPlan.length === 0) {
        showFeedback(
          'Could not build an execution plan for the selected node. Check parent-child links and breakpoint.',
          'error',
        );
        return;
      }

      const runId = executionRunIdRef.current + 1;
      executionRunIdRef.current = runId;

      setIsExecuting(true);
      setExecutionProgress(0);
      setExecutionTargetNodeId(targetNodeId || null);
      setExecutionMode(mode);
      setExecutionLogs([]);
      handleCloseEdit();
      handleCloseDeleteDialog();
      if (mode === 'breakpoint' && targetNodeId) {
        appendExecutionLog(`Started execution to breakpoint node ${targetNodeId}.`);
      } else if (targetNodeId) {
        appendExecutionLog(`Started partial execution to node ${targetNodeId}.`);
      } else {
        appendExecutionLog('Started full graph execution.');
      }

      const resetNodesById = {};
      nodeIds.forEach((nodeId) => {
        const node = currentGraph.nodesById[nodeId];
        resetNodesById[nodeId] = {
          ...node,
          status: 'pending',
          patientCount: null,
          isExecuted: false,
        };
      });

      applyGraphAction(
        {
          type: GRAPH_ACTIONS.SET_GRAPH,
          payload: {
            ...currentGraph,
            nodesById: resetNodesById,
            lastError: null,
          },
        },
        { recordHistory: true },
      );

      const countsByNode = {};

      try {
        for (let index = 0; index < executionPlan.length; index += 1) {
          if (executionRunIdRef.current !== runId) return;

          const nodeId = executionPlan[index];
          const node = graphStateRef.current.nodesById?.[nodeId];
          if (!node) continue;

          applyGraphAction(
            {
              type: GRAPH_ACTIONS.UPDATE_NODE,
              payload: {
                nodeId,
                patch: {
                  status: 'executing',
                  patientCount: null,
                  isExecuted: false,
                },
              },
            },
            { recordHistory: false },
          );

          await sleep(randomInt(500, 1000));
          if (executionRunIdRef.current !== runId) return;

          const currentNode = graphStateRef.current.nodesById?.[nodeId] || node;
          let nextStatus = 'completed';
          let nextPatientCount = null;
          let nextIsExecuted = false;

          if (index === 0) {
            nextPatientCount = randomInt(500, 5000);
            nextIsExecuted = true;
          } else {
            const parentId = currentNode.parentId;
            const parentCount = Number.isFinite(countsByNode[parentId]) ? countsByNode[parentId] : null;

            if (!Number.isFinite(parentCount) || parentCount < 10 || Math.random() < 0.04) {
              nextStatus = 'error';
            } else {
              const criteriaCount =
                (currentNode.includeConditions?.length || 0) + (currentNode.excludeConditions?.length || 0);
              const minimumFactor = 0.4;
              const maximumFactor = Math.max(0.45, 0.9 - Math.min(0.35, criteriaCount * 0.05));
              const factor = minimumFactor + Math.random() * Math.max(0.001, maximumFactor - minimumFactor);
              nextPatientCount = Math.max(10, Math.floor(parentCount * factor));
              nextIsExecuted = true;
            }
          }

          if (nextStatus === 'error') {
            countsByNode[nodeId] = null;
            appendExecutionLog(`${currentNode.name}: execution failed.`);
          } else {
            countsByNode[nodeId] = nextPatientCount;
            appendExecutionLog(`${currentNode.name}: ${nextPatientCount.toLocaleString()} patients.`);
          }

          applyGraphAction(
            {
              type: GRAPH_ACTIONS.UPDATE_NODE,
              payload: {
                nodeId,
                patch: {
                  status: nextStatus,
                  patientCount: nextPatientCount,
                  isExecuted: nextIsExecuted,
                },
              },
            },
            { recordHistory: false },
          );

          setExecutionProgress(Math.round(((index + 1) / executionPlan.length) * 100));
        }

        if (mode === 'breakpoint' && targetNodeId) {
          appendExecutionLog(`Breakpoint reached at node ${targetNodeId}.`);
        }
        appendExecutionLog('Execution completed.');

        const finishedAtIso = new Date().toISOString();
        setLastExecutionAt(finishedAtIso);
        applyGraphAction(
          {
            type: GRAPH_ACTIONS.SET_GRAPH,
            payload: {
              ...graphStateRef.current,
              metadata: {
                ...graphStateRef.current.metadata,
                lastExecutedAt: finishedAtIso,
              },
            },
          },
          { recordHistory: false },
        );

        showFeedback(
          mode === 'breakpoint' && targetNodeId
            ? `Execution completed at breakpoint node ${targetNodeId}.`
            : targetNodeId
              ? `Execution completed up to node ${targetNodeId}.`
              : 'Graph execution completed.',
          'success',
        );
      } finally {
        if (executionRunIdRef.current === runId) {
          setIsExecuting(false);
          setExecutionTargetNodeId(null);
          setExecutionMode('full');
        }
      }
    },
    [
      appendExecutionLog,
      applyGraphAction,
      handleCloseDeleteDialog,
      handleCloseEdit,
      isExecuting,
      showFeedback,
    ],
  );

  const handleExecuteGraph = useCallback(() => {
    runExecutionSimulation(null, 'full');
  }, [runExecutionSimulation]);

  const handleExecuteToHere = useCallback(
    (nodeId) => {
      runExecutionSimulation(nodeId, 'node');
    },
    [runExecutionSimulation],
  );

  const handleExecuteToBreakpoint = useCallback(() => {
    const targetNodeId = graphStateRef.current.metadata?.breakpointNodeId || null;
    if (!targetNodeId) {
      showFeedback('Set a breakpoint on a node first.', 'warning');
      return;
    }
    runExecutionSimulation(targetNodeId, 'breakpoint');
  }, [runExecutionSimulation, showFeedback]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const isEditableElement = (target) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName?.toLowerCase();
      return tag === 'input' || tag === 'textarea' || target.isContentEditable;
    };

    const handleShortcut = (event) => {
      const key = String(event.key || '').toLowerCase();
      const hasModifier = event.ctrlKey || event.metaKey;
      if (!hasModifier) return;

      if (key === 's') {
        event.preventDefault();
        persistDraftNow();
        return;
      }

      if (key === 'k' && event.shiftKey) {
        event.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      if (isEditableElement(event.target)) return;

      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        if (!isExecuting) handleUndo();
        return;
      }

      if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        if (!isExecuting) handleRedo();
        return;
      }

      if (key === 'enter') {
        event.preventDefault();
        if (!isExecuting) handleExecuteGraph();
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => {
      window.removeEventListener('keydown', handleShortcut);
    };
  }, [handleExecuteGraph, handleRedo, handleUndo, isExecuting, persistDraftNow]);

  const nodeTypes = useMemo(
    () => ({
      cohortNode: (props) => (
        <CohortNode
          {...props}
          onEdit={isExecuting ? undefined : handleEditNode}
          onDelete={isExecuting ? undefined : handleRequestDeleteNode}
          onAddChild={isExecuting ? undefined : handleAddChild}
          onInsert={isExecuting ? undefined : handleInsertBetween}
          onExecuteToHere={isExecuting ? undefined : handleExecuteToHere}
          onToggleBreakpoint={isExecuting ? undefined : handleToggleBreakpoint}
          onRename={isExecuting ? undefined : handleRenameNode}
        />
      ),
    }),
    [
      handleAddChild,
      handleEditNode,
      handleExecuteToHere,
      handleInsertBetween,
      handleRenameNode,
      handleRequestDeleteNode,
      handleToggleBreakpoint,
      isExecuting,
    ],
  );

  const defaultEdgeOptions = useMemo(
    () => ({
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { strokeWidth: 2 },
    }),
    [],
  );

  const handleLoadDemo = useCallback(() => {
    try {
      const demoGraph = buildGraphStateFromReactFlow(demoNodes, demoEdges, 'Cohort Graph Demo');
      applyGraphAction(
        {
          type: GRAPH_ACTIONS.SET_GRAPH,
          payload: demoGraph,
        },
        { recordHistory: true },
      );
      showFeedback('Demo graph loaded.', 'success');
    } catch (error) {
      showFeedback(`Failed to load demo graph: ${error.message}`, 'error');
    }
  }, [applyGraphAction, showFeedback]);

  const handleGenerateLargeDemo = useCallback(() => {
    try {
      const largeDemo = generateDemoTree(60);
      const demoGraph = buildGraphStateFromReactFlow(
        largeDemo.nodes,
        largeDemo.edges,
        'Large Cohort Graph Demo',
      );

      applyGraphAction(
        {
          type: GRAPH_ACTIONS.SET_GRAPH,
          payload: demoGraph,
        },
        { recordHistory: true },
      );
      showFeedback('60-node demo graph generated.', 'success');
    } catch (error) {
      showFeedback(`Failed to generate large demo graph: ${error.message}`, 'error');
    }
  }, [applyGraphAction, showFeedback]);

  const handleClear = useCallback(() => {
    const changed = applyGraphAction(
      {
        type: GRAPH_ACTIONS.SET_GRAPH,
        payload: createEmptyGraphState('Cohort Graph Draft'),
      },
      { recordHistory: true },
    );
    if (changed) {
      showFeedback('Graph cleared.', 'info');
    }
  }, [applyGraphAction, showFeedback]);

  const handleAutoLayout = useCallback(() => {
    if (nodes.length === 0) return;
    setLayoutVersion((value) => value + 1);
    showFeedback('Auto-layout applied.', 'success');
  }, [nodes.length, showFeedback]);

  const handleTriggerImport = useCallback(() => {
    importFileInputRef.current?.click();
  }, []);

  const handleImportFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        const importedGraph = deserializeImportedGraph(parsed);

        applyGraphAction(
          {
            type: GRAPH_ACTIONS.SET_GRAPH,
            payload: importedGraph,
          },
          { recordHistory: true },
        );
        showFeedback('Graph imported successfully.', 'success');
      } catch (error) {
        showFeedback(`Import failed: ${error.message}`, 'error');
      }
    },
    [applyGraphAction, showFeedback],
  );

  const handleExportGraph = useCallback(() => {
    const payload = serializeGraphForExport(graphStateRef.current);
    downloadGraphJson(payload);
    showFeedback('Graph exported as JSON.', 'success');
  }, [showFeedback]);

  const handleExportSvg = useCallback(() => {
    try {
      if (nodes.length === 0) {
        showFeedback('Create or load a graph before exporting.', 'warning');
        return;
      }
      downloadSnapshotSvg(nodes, edges);
      showFeedback('Graph exported as SVG.', 'success');
    } catch (error) {
      showFeedback(`SVG export failed: ${error.message}`, 'error');
    }
  }, [edges, nodes, showFeedback]);

  const handleExportPng = useCallback(async () => {
    try {
      if (nodes.length === 0) {
        showFeedback('Create or load a graph before exporting.', 'warning');
        return;
      }
      await downloadSnapshotPng(nodes, edges);
      showFeedback('Graph exported as PNG.', 'success');
    } catch (error) {
      showFeedback(`PNG export failed: ${error.message}`, 'error');
    }
  }, [edges, nodes, showFeedback]);

  const lastExecutionLabel = useMemo(() => {
    if (!lastExecutionAt) return 'Never';
    try {
      return new Date(lastExecutionAt).toLocaleString();
    } catch {
      return 'Unknown';
    }
  }, [lastExecutionAt]);

  const deleteDescendantCount = deleteDialog.descendantIds.length;
  const deleteHasChildren = deleteDescendantCount > 0;

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            Home
          </Link>
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/cohorts')}
          >
            Kohorten
          </Link>
          <Typography color="text.primary">Cohort Graph Builder</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Cohort Graph Builder
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Visualize and refine cohorts as an acyclic graph.
          </Typography>
        </Box>

        {showWelcomeTour ? (
          <Alert
            severity="info"
            variant="outlined"
            sx={{ mb: 2, alignItems: 'flex-start' }}
            action={
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => setShortcutsOpen(true)}>
                  Shortcuts
                </Button>
                <Button size="small" variant="contained" onClick={handleDismissWelcomeTour}>
                  Got it
                </Button>
              </Stack>
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              First time here?
            </Typography>
            <Typography variant="body2">
              1. Create a root node or load a demo.
              <br />
              2. Use node actions to add, edit, insert, and execute.
              <br />
              3. Use Ctrl/Cmd+S to save draft and Ctrl/Cmd+Shift+K to view shortcuts.
            </Typography>
          </Alert>
        ) : null}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Tip: Load a demo graph to test zoom/pan, edges, auto-layout, and CRUD operations.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                '& .MuiButton-root': {
                  minWidth: { xs: 'auto', sm: 110 },
                },
                '& .MuiButtonBase-root:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                },
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                aria-label="Create root node"
                onClick={handleCreateRootNode}
                disabled={nodes.length > 0 || isExecuting}
              >
                Create Root
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RocketLaunchIcon />}
                aria-label="Load demo graph"
                onClick={handleLoadDemo}
                disabled={isExecuting}
              >
                Load Demo
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RocketLaunchIcon />}
                aria-label="Generate 60-node demo graph"
                onClick={handleGenerateLargeDemo}
                disabled={isExecuting}
              >
                60 Nodes
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoFixHighIcon />}
                aria-label="Apply automatic layout"
                onClick={handleAutoLayout}
                disabled={nodes.length === 0 || isExecuting}
              >
                Auto-Layout
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrowIcon />}
                aria-label="Execute complete graph"
                onClick={handleExecuteGraph}
                disabled={nodes.length === 0 || isExecuting}
              >
                Execute Graph
              </Button>
              <Tooltip
                title={
                  breakpointNodeId
                    ? `Execute from root to breakpoint "${breakpointNodeLabel}".`
                    : 'Set a breakpoint from a node context menu first.'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OutlinedFlagIcon />}
                    aria-label="Execute to selected breakpoint"
                    onClick={handleExecuteToBreakpoint}
                    disabled={nodes.length === 0 || isExecuting || !breakpointNodeId}
                  >
                    To Breakpoint
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                aria-label="Import graph JSON file"
                onClick={handleTriggerImport}
                disabled={isExecuting}
              >
                Import JSON
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                aria-label="Export graph as JSON"
                onClick={handleExportGraph}
                disabled={nodes.length === 0 || isExecuting}
              >
                Export JSON
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ImageIcon />}
                aria-label="Export graph as PNG image"
                onClick={handleExportPng}
                disabled={nodes.length === 0 || isExecuting}
              >
                Export PNG
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ImageIcon />}
                aria-label="Export graph as SVG image"
                onClick={handleExportSvg}
                disabled={nodes.length === 0 || isExecuting}
              >
                Export SVG
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                aria-label="Clear graph"
                onClick={handleClear}
                disabled={nodes.length === 0 || isExecuting}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UndoIcon />}
                aria-label="Undo latest change"
                onClick={handleUndo}
                disabled={historyPast.length === 0 || isExecuting}
              >
                Undo
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RedoIcon />}
                aria-label="Redo latest change"
                onClick={handleRedo}
                disabled={historyFuture.length === 0 || isExecuting}
              >
                Redo
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<KeyboardIcon />}
                aria-label="Open keyboard shortcuts"
                onClick={() => setShortcutsOpen(true)}
              >
                Shortcuts
              </Button>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 1 }}>
              Layout:
            </Typography>
            <Button
              size="small"
              variant={layoutDirection === 'LR' ? 'contained' : 'outlined'}
              onClick={() => setLayoutDirection('LR')}
              disabled={isExecuting}
            >
              Left -&gt; Right
            </Button>
            <Button
              size="small"
              variant={layoutDirection === 'TB' ? 'contained' : 'outlined'}
              onClick={() => setLayoutDirection('TB')}
              disabled={isExecuting}
            >
              Top -&gt; Bottom
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Nodes: <strong>{graphStatistics.totalNodes}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Max depth: <strong>{graphStatistics.maxDepth}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total patients: <strong>{graphStatistics.totalPatients.toLocaleString()}</strong>
            </Typography>
            <Typography variant="caption" color={breakpointNodeId ? 'warning.dark' : 'text.secondary'}>
              Breakpoint: <strong>{breakpointNodeLabel || 'None'}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last execution: <strong>{lastExecutionLabel}</strong>
            </Typography>
          </Stack>

          <Box sx={{ mt: 1.5 }} role="status" aria-live="polite">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Typography variant="caption" color={isExecuting ? 'warning.main' : 'text.secondary'}>
                {isExecuting
                  ? executionTargetNodeId
                    ? executionMode === 'breakpoint'
                      ? `Executing to breakpoint ${executionTargetNodeId}...`
                      : `Executing to node ${executionTargetNodeId}...`
                    : 'Executing full graph...'
                  : 'Execution idle'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Progress: <strong>{executionProgress}%</strong>
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={executionProgress}
              sx={{ mt: 0.75, height: 6, borderRadius: 99 }}
            />
          </Box>

          {executionLogs.length > 0 ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Execution log
              </Typography>
              <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 130, overflow: 'auto' }}>
                <List dense disablePadding>
                  {executionLogs.map((entry, index) => (
                    <ListItem key={`${index}-${entry}`} sx={{ py: 0.35, px: 1 }}>
                      <ListItemText
                        primaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'monospace' } }}
                        primary={entry}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          ) : null}

          {graphState.lastError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {graphState.lastError}
            </Alert>
          ) : null}
        </Paper>

        <Paper
          sx={{
            height: { xs: '56vh', sm: '62vh', md: '70vh' },
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {nodes.length === 0 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center',
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Create your first cohort
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520 }}>
                Start with an empty root node, or load a demo graph to test the full node CRUD workflow.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" aria-label="Create root node from empty graph state" onClick={handleCreateRootNode}>
                  Create Root Node
                </Button>
                <Button variant="outlined" aria-label="Load demo graph from empty graph state" onClick={handleLoadDemo}>
                  Load Demo Graph
                </Button>
                <Button variant="outlined" aria-label="Go back to cohorts page" onClick={() => navigate('/cohorts')}>
                  Go to Cohorts
                </Button>
              </Stack>
            </Box>
          ) : (
            <ReactFlow
              key={`graph-${layoutDirection}-${layoutVersion}`}
              nodes={nodes}
              edges={edges}
              deleteKeyCode={null}
              nodeTypes={nodeTypes}
              defaultEdgeOptions={defaultEdgeOptions}
              fitView
              minZoom={0.2}
              maxZoom={2}
              ariaLabel="Cohort graph builder canvas"
            >
              <Background gap={18} size={1} />
              <Controls />
              <MiniMap
                zoomable
                pannable
                nodeStrokeColor={() => '#ed7d31'}
                nodeColor={() => '#ffffff'}
                nodeBorderRadius={6}
              />
            </ReactFlow>
          )}
        </Paper>

        <Dialog
          open={Boolean(editingNode)}
          onClose={handleCloseEdit}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallScreen}
        >
          <DialogTitle>Edit Cohort Node</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                label="Label"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                fullWidth
                size="small"
                autoFocus
              />
              <FormControl fullWidth size="small">
                <InputLabel id="cohort-node-status-label">Status</InputLabel>
                <Select
                  labelId="cohort-node-status-label"
                  value={editStatus}
                  label="Status"
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="unedited">Unedited</MenuItem>
                  <MenuItem value="edited">Edited</MenuItem>
                  <MenuItem value="executing">Executing</MenuItem>
                  <MenuItem value="executed">Executed</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                </Select>
              </FormControl>

              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Include Criteria
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => handleAddCriterion('include')}
                  >
                    Add Include
                  </Button>
                </Stack>
                {editIncludeCriteria.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No include criteria defined yet.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {editIncludeCriteria.map((criterion) => (
                      <Stack
                        key={criterion.id}
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                      >
                        <CriteriaParameterAutocomplete
                          label="Field"
                          value={criterion.field}
                          onChange={(nextValue) =>
                            handleUpdateCriterion('include', criterion.id, 'field', nextValue)
                          }
                        />
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <InputLabel id={`include-operator-${criterion.id}`}>Operator</InputLabel>
                          <Select
                            labelId={`include-operator-${criterion.id}`}
                            value={criterion.operator}
                            label="Operator"
                            onChange={(e) =>
                              handleUpdateCriterion('include', criterion.id, 'operator', e.target.value)
                            }
                          >
                            {CRITERIA_OPERATORS.map((operator) => (
                              <MenuItem key={operator.value} value={operator.value}>
                                {operator.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Value"
                          size="small"
                          value={criterion.value}
                          onChange={(e) =>
                            handleUpdateCriterion('include', criterion.id, 'value', e.target.value)
                          }
                          fullWidth
                        />
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="delete include criterion"
                          onClick={() => handleDeleteCriterion('include', criterion.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>

              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Exclude Criteria
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => handleAddCriterion('exclude')}
                  >
                    Add Exclude
                  </Button>
                </Stack>
                {editExcludeCriteria.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No exclude criteria defined yet.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {editExcludeCriteria.map((criterion) => (
                      <Stack
                        key={criterion.id}
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                      >
                        <CriteriaParameterAutocomplete
                          label="Field"
                          value={criterion.field}
                          onChange={(nextValue) =>
                            handleUpdateCriterion('exclude', criterion.id, 'field', nextValue)
                          }
                        />
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <InputLabel id={`exclude-operator-${criterion.id}`}>Operator</InputLabel>
                          <Select
                            labelId={`exclude-operator-${criterion.id}`}
                            value={criterion.operator}
                            label="Operator"
                            onChange={(e) =>
                              handleUpdateCriterion('exclude', criterion.id, 'operator', e.target.value)
                            }
                          >
                            {CRITERIA_OPERATORS.map((operator) => (
                              <MenuItem key={operator.value} value={operator.value}>
                                {operator.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Value"
                          size="small"
                          value={criterion.value}
                          onChange={(e) =>
                            handleUpdateCriterion('exclude', criterion.id, 'value', e.target.value)
                          }
                          fullWidth
                        />
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="delete exclude criterion"
                          onClick={() => handleDeleteCriterion('exclude', criterion.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Saving updates node label, status, and include/exclude criteria.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEdit}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={!editLabel.trim()}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
          maxWidth="sm"
          fullWidth
          fullScreen={isSmallScreen}
        >
          <DialogTitle>Delete Cohort Node</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25}>
              <Typography variant="body2">
                Are you sure you want to delete <strong>{deleteDialog.nodeLabel || 'this node'}</strong>?
              </Typography>
              {deleteHasChildren ? (
                <>
                  <Typography variant="body2" color="warning.main">
                    This node has {deleteDescendantCount} descendant node
                    {deleteDescendantCount === 1 ? '' : 's'}.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Deleting only this node is not allowed because it would create orphaned nodes.
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  This node has no children, so only this node will be removed.
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            {deleteHasChildren ? (
              <>
                <Button color="error" disabled>
                  Delete Only (Not Allowed)
                </Button>
                <Button variant="contained" color="error" onClick={() => handleConfirmDeleteNode(true)}>
                  Delete Node + Descendants
                </Button>
              </>
            ) : (
              <Button variant="contained" color="error" onClick={() => handleConfirmDeleteNode(false)}>
                Delete Node
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog
          open={shortcutsOpen}
          onClose={() => setShortcutsOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isSmallScreen}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyboardIcon fontSize="small" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText component="div">
              <Stack spacing={1.25}>
                <Typography variant="body2">
                  <strong>Ctrl/Cmd + S</strong>: Save draft locally
                </Typography>
                <Typography variant="body2">
                  <strong>Ctrl/Cmd + Z</strong>: Undo
                </Typography>
                <Typography variant="body2">
                  <strong>Ctrl/Cmd + Shift + Z</strong> or <strong>Ctrl/Cmd + Y</strong>: Redo
                </Typography>
                <Typography variant="body2">
                  <strong>Ctrl/Cmd + Enter</strong>: Execute graph
                </Typography>
                <Typography variant="body2">
                  <strong>Ctrl/Cmd + Shift + K</strong>: Open shortcuts help
                </Typography>
              </Stack>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShortcutsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={feedback.open}
          autoHideDuration={3500}
          onClose={handleCloseFeedback}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseFeedback}
            severity={feedback.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {feedback.message}
          </Alert>
        </Snackbar>

        <input
          ref={importFileInputRef}
          type="file"
          accept="application/json"
          style={{ display: 'none' }}
          aria-label="Import graph JSON file input"
          onChange={handleImportFile}
        />
      </Box>
    </MainLayout>
  );
};

const CohortGraphBuilder = () => {
  const initialGraphState = useMemo(() => readDraftGraphState(), []);

  return (
    <GraphStateProvider initialState={initialGraphState}>
      <CohortGraphBuilderContent />
    </GraphStateProvider>
  );
};

export default CohortGraphBuilder;
